import { Button, Flex } from '@aws-amplify/ui-react';
import { useContext, useEffect, useMemo, useState } from 'react';
import 'react-querybuilder/dist/query-builder.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { PeopleResults } from '../components/PeopleResults';
import QueryEditor from '../custom-components/QueryEditor';
import { SearchHistoryPanel } from '../custom-components/SearchHistoryPanel';
import { GlobalContext, GlobalContextProps } from '../global-context';
import { useSearchHistory } from '../hooks/useSearchHistory';
import { ContactsSearchParams } from '../utils/ContactsSearchParams';
import General from '../utils/GeneralUtils';
import { LocalStorageKeys, LocalStorageService } from '../utils/LocalStorageService';
import { stripLastSeenFromQ, withDefaultLastSeen } from '../utils/peopleSearchDefaults';
import { convertQtoQuery } from '../utils/queryConverter';

export function People() {

  const globalContext = useContext(GlobalContext);
  General.setTitle(document, 'People', globalContext);

  const location = useLocation();
  const navigate = useNavigate();

  const [currentSearchParams, setCurrentSearchParams] = useState<ContactsSearchParams>();
  const [newSearchParams, setNewSearchParams] = useState<ContactsSearchParams>();
  const [isSearchHistoryPanelOpen, setIsSearchHistoryPanelOpen] = useState(
    LocalStorageService.get<boolean>(LocalStorageKeys.IS_SEARCH_HISTORY_PANEL_OPEN)
  );

  // Search history hook - pass userId for Pusher subscription
  const userId = globalContext.authInfo?.userAttributes?.sub || '';
  const teamId = globalContext.authInfo?.teamId || '';
  const searchHistory = useSearchHistory({ entity: 'CONTACT', teamId, source: 'PEOPLE_SEARCH', userId });

  // The URL's q, without the lastSeen rule old URLs and saved searches may still carry; the
  // editor never shows it.
  const q = useMemo(
    () => stripLastSeenFromQ(String(General.getQueryParamValueOrNull(new URLSearchParams(location.search), 'q') || '')),
    [location.search]
  );
  // The query the API actually runs: the editor's rules plus the hidden 6-month lastSeen default.
  // Search and export both use it, so the export count always matches what the user saw. From
  // the URL directly, so the list and its page change together.
  const effectiveQuery = useMemo(() => withDefaultLastSeen(convertQtoQuery(q)), [q]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const params = {
      page: General.getQueryParamValueOrNull(queryParams, 'page') || 1,
      pageToken: General.getQueryParamValueOrNull(queryParams, 'pageToken'),
      sortBy: General.getQueryParamValueOrNull(queryParams, 'sortBy') || 'relevance',
      sortDir: General.getQueryParamValueOrNull(queryParams, 'sortDir') || 'desc',
      q,
    }
    setCurrentSearchParams(new ContactsSearchParams(params))
    setNewSearchParams(new ContactsSearchParams(params))
  }, [location.search]);

  function handleOnSearch() {
    navigate(`?${newSearchParams?.serialize('1')}`);
  }

  function handleKeyDown(e: any) {
    if (e.key === 'Enter') {
      handleOnSearch();
    }
  };

  function handleToggleSearchHistoryPanel() {
    const newValue = !isSearchHistoryPanelOpen;
    setIsSearchHistoryPanelOpen(newValue);
    LocalStorageService.set(LocalStorageKeys.IS_SEARCH_HISTORY_PANEL_OPEN, newValue);
  }


  return <GlobalContext.Consumer>
    {
      (globalContextProps: GlobalContextProps) => <>
        <div style={{ display: 'flex', minHeight: '100vh' }}>
          <SearchHistoryPanel
            basePath="/directory/people"
            teamShared={searchHistory.teamShared}
            starred={searchHistory.starred}
            recent={searchHistory.recent}
            currentUserId={userId}
            onToggleStar={searchHistory.toggleStar}
            onArchive={searchHistory.archiveSearch}
            onRename={searchHistory.renameSearch}
            onShareWithTeam={searchHistory.shareWithTeam}
            onUnshareFromTeam={searchHistory.unshareFromTeam}
            isOpen={isSearchHistoryPanelOpen}
            onTogglePanel={handleToggleSearchHistoryPanel}
          />
          <div style={{ flex: 1, overflow: 'auto', position: 'relative', zIndex: 1 }}>
            <div className="content content-1000">
              <div className="main-card">
                <div style={{ padding: 10, position: 'relative' }} onKeyDown={handleKeyDown}>
                  {
                    currentSearchParams
                      ? <QueryEditor
                          initialQuery={currentSearchParams?.q.serialize()}
                          supportedFields={globalContextProps.environment.entities.contact.queryEditorSupportedFields}
                          teamTags={[]}
                          type='contacts'
                          authInfo={globalContext.authInfo}
                          onChange={(query: string | null) => {
                            setNewSearchParams(prevState => new ContactsSearchParams({
                              ...prevState?.raw(),
                              ...{ q: query }
                            }))
                          }}
                        />
                      : <></>
                  }
                  <Flex justifyContent='flex-end' marginTop={10} gap={5}>
                    <Button
                      variation='primary'
                      size='small'
                      isDisabled={currentSearchParams?.serialized === newSearchParams?.serialized}
                      onClick={handleOnSearch}
                      loadingText="Searching"
                    >Search</Button>
                  </Flex>
                </div>
              </div>
            </div>
            <PeopleResults
              conditions={effectiveQuery}
              searchKey={q}
              source="PEOPLE_SEARCH"
              savedSearchId={searchHistory.currentSearchId}
            />
          </div>
        </div>
      </>
    }
  </GlobalContext.Consumer>
}
